// Channel feed — editorial redesign.
//
// Visual hierarchy from the prototype (Page 04 + Page 21):
//   1. Sticky header — avatar + serif channel name + Mono meta + cashback chip
//   2. Date strip — "26 МАЯ · СРЕДА" Plex Mono uppercase
//   3. Section heading — "Подборка дня" in Plex Serif at hero scale
//   4. Persimmon attention strip when any СП closes in <24h
//   5. Filter chips (channel categories)
//   6. Post cards — image-led, serif title, Plex Serif price w/ strikethrough,
//      Mono "brand · category", ProgressArc replacing the bar for СП posts
//
// Functional behavior unchanged — same callbacks, same API methods. Just
// reskinned to match the editorial system.
import { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { api } from "../api.js";
import { T, T_NEW } from "../theme.js";
import { I } from "../icons.jsx";
import { Av, Btn, Mono, Serif, ProgressArc, StatusPill } from "./ui.jsx";
import { useToast } from "./toast.jsx";
import { useT, plural, useLang, localeFor } from "../i18n.jsx";
import { chName } from "../channelLocale.js";
import { OrderModal } from "./OrderModal.jsx";
import { ForwardModal } from "./ForwardModal.jsx";
import { ForwardHistoryModal } from "./ForwardHistoryModal.jsx";
import { CodeLookupModal } from "./CodeLookupModal.jsx";
import { WarehouseEditModal } from "./WarehouseEditModal.jsx";
import { cartAdd } from "../cart.js";
import { thumbUrl } from "../media.js";

// Locale-aware "26 МАЯ · СРЕДА" / "26 MAY · WEDNESDAY". Uses Intl
// so the same helper renders Russian for ru and English for en;
// the format is day-month-weekday, uppercased to match the editorial
// kicker treatment. Defensive try/catch so a missing-locale UA
// (older Safari etc.) still renders something readable.
function formatToday(lang) {
  const locale = localeFor(lang);
  const d = new Date();
  try {
    const fmt = new Intl.DateTimeFormat(locale, {
      day: "numeric", month: "long", weekday: "long",
    });
    const parts = fmt.formatToParts(d);
    const day = parts.find(p => p.type === "day")?.value || "";
    const month = parts.find(p => p.type === "month")?.value || "";
    const weekday = parts.find(p => p.type === "weekday")?.value || "";
    return `${day} ${month} · ${weekday}`.toUpperCase();
  } catch {
    return d.toDateString().toUpperCase();
  }
}

// "2026-06-19" → localized short date ("19 июн.", "Jun 19", "19 jun"). Used
// for the sale-valid-through badge; never baked into the card image.
function fmtShortDate(iso, lang) {
  try {
    const [y, m, d] = (iso || "").split("-").map(Number);
    if (!y || !m || !d) return iso;
    return new Intl.DateTimeFormat(localeFor(lang), { day: "numeric", month: "short" })
      .format(new Date(y, m - 1, d));
  } catch {
    return iso;
  }
}

// A title/description is "blank" when it's empty or only dashes/whitespace.
// The Telegram importer (import_telegram_export.py) stores "—" placeholders
// because the API requires a non-empty title/body, but those rows are meant
// to show the photo + price only until staff fill in the real name/copy.
// Suppressing dash-only text keeps imported (and unfinished) cards clean.
const isBlankText = (s) => !s || /^[\s—–-]*$/.test(s);

export function ChannelFeed({
  user, active, channels, channelPosts, isMobile,
  // Staff-only "Archived" view: when showArchived is true the parent passes
  // the archived posts as channelPosts; archivedCount drives the toggle badge.
  showArchived, onToggleArchived, archivedCount = 0,
  onBack, onCreatePost, onAfterPostChange, onOrderIntent,
  // Passed through to ForwardModal so its empty-state hints can
  // deep-link to Settings → Forward without forcing the reseller to
  // close the modal and hunt for the right tab.
  onOpenForwardSettings,
  // Passed to the "Источник в Premium" pill on each card so free-tier
  // resellers tapping it land directly on the upgrade pitch.
  onOpenPremiumSettings,
  // CodeLookupModal calls this with a channel slug to navigate to
  // that channel's feed. Same shape as the sidebar's channel-click
  // handler — App.jsx owns the active-channel state.
  onJumpToChannel,
}) {
  const toast = useToast();
  const t = useT();
  const lang = useLang();
  const locale = localeFor(lang);
  const activeChannel = (channels ?? []).find(c => c.slug === active.slug || c.id === active.id);
  const cashbackPct = activeChannel?.cashbackPct ?? 0;
  const [orderingPost, setOrderingPost] = useState(null);

  // View mode — list (single column) or grid (multi-column auto-fit).
  // List is the editorial default; grid is for browsing dense catalogues
  // like scraped retailer feeds (curated-shoes etc.) where the user wants
  // to scan many products at a glance. Persisted per-device so the user
  // doesn't have to re-pick on every visit. SSR-safe via the lazy init.
  const VIEW_KEY = "buyergram.channelFeedView";
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === "undefined") return "list";
    return window.localStorage?.getItem(VIEW_KEY) === "grid" ? "grid" : "list";
  });
  useEffect(() => {
    try { window.localStorage?.setItem(VIEW_KEY, viewMode); } catch {}
  }, [viewMode]);

  // Floating scroll controls + incremental ("lazy") rendering for long feeds.
  // The feed scrolls inside its own overflow container (not the window), so
  // we drive that element via a ref. Rather than mount all 80-500 cards up
  // front, we render PAGE cards and reveal the next page as the user nears
  // the bottom — much lighter initial paint.
  const PAGE = 24;
  const feedScrollRef = useRef(null);
  const pendingBottomRef = useRef(false);
  const [visibleCount, setVisibleCount] = useState(PAGE);
  const [showScrollUp, setShowScrollUp] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const updateScrollNav = () => {
    const el = feedScrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollUp(el.scrollTop > 500);
    setShowScrollDown(dist > 500);
    // Reveal the next page a touch before the end so it feels seamless.
    if (dist < 900) {
      setVisibleCount(c => (c < filteredPosts.length ? Math.min(c + PAGE, filteredPosts.length) : c));
    }
  };
  const scrollFeed = (where) => {
    const el = feedScrollRef.current;
    if (!el) return;
    if (where === "top") { el.scrollTo({ top: 0, behavior: "smooth" }); return; }
    // "To bottom": reveal everything first so we can land on the last card.
    if (visibleCount < filteredPosts.length) {
      pendingBottomRef.current = true;
      setVisibleCount(filteredPosts.length);
    } else {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
  };
  // Re-measure when the rendered list changes (channel switch, view toggle,
  // posts (re)load) so the buttons appear/disappear without needing a scroll.
  useEffect(() => { updateScrollNav(); }, [active, viewMode, channelPosts]);
  // After "to bottom" reveals the remaining cards, scroll to the real end.
  useEffect(() => {
    if (!pendingBottomRef.current) return;
    pendingBottomRef.current = false;
    feedScrollRef.current?.scrollTo({ top: feedScrollRef.current.scrollHeight, behavior: "smooth" });
  }, [visibleCount]);

  const navBtnStyle = {
    width: 42, height: 42, borderRadius: 21,
    background: T_NEW.paperRaised, color: T_NEW.ink,
    border: `1px solid ${T_NEW.ink12}`, boxShadow: T_NEW.shadowCard,
    cursor: "pointer", fontSize: 20, lineHeight: 1,
    display: "flex", alignItems: "center", justifyContent: "center",
  };

  // Filter + sort state. Kept client-side because (a) Phase-0 channels
  // are small enough that browser-side filtering is instant, (b) it
  // saves API round-trips, and (c) the user can change filters without
  // re-fetching. When channels grow past a few thousand posts we'll
  // need to push this server-side.
  const [filters, setFilters] = useState({
    brands: new Set(),       // empty = all brands
    priceMinDollars: "",     // empty = no lower bound
    priceMaxDollars: "",     // empty = no upper bound
    minDiscountPct: 0,       // 0 = any
    query: "",               // keyword search; matches title/brand/sku
    // Gender filter (migration 0055). "" = all; otherwise one of
    // 'women', 'men', 'kids', 'unisex'. Posts with NULL gender are
    // matched ONLY when filter is "" (so a reseller targeting women's
    // doesn't get noise from unclassified items).
    gender: "",
  });
  const [sortBy, setSortBy] = useState("newest");

  // Distinct brands present in this channel — drives the brand filter
  // dropdown. Sorted alphabetically so the dropdown is scannable.
  const uniqueBrands = useMemo(() => {
    const set = new Set();
    (channelPosts ?? []).forEach(p => { if (p.brand) set.add(p.brand); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, locale));
  }, [channelPosts, locale]);

  // Filtered + sorted posts derived from the raw channelPosts.
  // Filters compose AND-wise. Sort is applied last.
  const filteredPosts = useMemo(() => {
    let posts = (channelPosts ?? []).slice();
    if (filters.brands.size > 0) {
      posts = posts.filter(p => p.brand && filters.brands.has(p.brand));
    }
    if (filters.gender) {
      // Strict gender match. NULL gender doesn't pass — a reseller
      // who's explicitly picked Women's doesn't want unclassified
      // items mixed in.
      posts = posts.filter(p => p.gender === filters.gender);
    }
    if (filters.minDiscountPct > 0) {
      posts = posts.filter(p => (p.discountPct ?? 0) >= filters.minDiscountPct);
    }
    const minCents = filters.priceMinDollars === "" ? null : Number(filters.priceMinDollars) * 100;
    const maxCents = filters.priceMaxDollars === "" ? null : Number(filters.priceMaxDollars) * 100;
    if (minCents != null && !Number.isNaN(minCents)) {
      posts = posts.filter(p => (p.priceCents ?? 0) >= minCents);
    }
    if (maxCents != null && !Number.isNaN(maxCents)) {
      posts = posts.filter(p => (p.priceCents ?? 0) <= maxCents);
    }
    // Keyword search — matches title, brand, publicCode, or upstream
    // SKU substring. publicCode is the customer-facing identifier
    // (migration 0038); upstream SKU lives in sourceUrl when present
    // (".../<sku>" suffix on scraped posts). sourceUrl is null for
    // free-tier accounts on premium-gated rows, in which case the
    // search just doesn't match there — fine, the publicCode covers
    // the "I have a code from a customer" case.
    const q = filters.query.trim().toLowerCase();
    if (q) {
      posts = posts.filter(p => {
        const hay = `${p.title ?? ""} ${p.brand ?? ""} ${p.publicCode ?? ""} ${p.sourceUrl ?? ""}`.toLowerCase();
        return hay.includes(q);
      });
    }
    switch (sortBy) {
      case "price-asc":
        posts.sort((a, b) => (a.priceCents ?? Infinity) - (b.priceCents ?? Infinity));
        break;
      case "price-desc":
        posts.sort((a, b) => (b.priceCents ?? -Infinity) - (a.priceCents ?? -Infinity));
        break;
      case "brand":
        posts.sort((a, b) => (a.brand ?? "").localeCompare(b.brand ?? "", locale));
        break;
      case "discount-desc":
        posts.sort((a, b) => (b.discountPct ?? 0) - (a.discountPct ?? 0));
        break;
      case "newest":
      default:
        posts.sort((a, b) => (b.publishedAt ?? 0) - (a.publishedAt ?? 0));
    }
    return posts;
  }, [channelPosts, filters, sortBy, locale]);

  const activeFilterCount =
    filters.brands.size +
    (filters.priceMinDollars !== "" ? 1 : 0) +
    (filters.priceMaxDollars !== "" ? 1 : 0) +
    (filters.minDiscountPct > 0 ? 1 : 0) +
    (filters.query.trim() !== "" ? 1 : 0) +
    (filters.gender ? 1 : 0);
  const clearFilters = () => setFilters({ brands: new Set(), priceMinDollars: "", priceMaxDollars: "", minDiscountPct: 0, query: "", gender: "" });

  // Reset the incremental window to the first page on a channel switch or a
  // filter/sort change — but NOT when channelPosts merely grows (pages
  // streaming in from the paginated fetch), so scroll position is preserved.
  useEffect(() => { setVisibleCount(PAGE); }, [active?.slug, filters, sortBy, showArchived]);

  // Gender counts for the chip badges. We compute over the
  // *pre-gender-filter* set so switching chips doesn't change the
  // visible counts. Brands + price + discount + query filters DO
  // apply so the count reflects what the user would see if they
  // tapped that chip.
  const genderCounts = useMemo(() => {
    let posts = (channelPosts ?? []).slice();
    if (filters.brands.size > 0) {
      posts = posts.filter(p => p.brand && filters.brands.has(p.brand));
    }
    if (filters.minDiscountPct > 0) {
      posts = posts.filter(p => (p.discountPct ?? 0) >= filters.minDiscountPct);
    }
    const minCents = filters.priceMinDollars === "" ? null : Number(filters.priceMinDollars) * 100;
    const maxCents = filters.priceMaxDollars === "" ? null : Number(filters.priceMaxDollars) * 100;
    if (minCents != null && !Number.isNaN(minCents)) {
      posts = posts.filter(p => (p.priceCents ?? 0) >= minCents);
    }
    if (maxCents != null && !Number.isNaN(maxCents)) {
      posts = posts.filter(p => (p.priceCents ?? 0) <= maxCents);
    }
    const q = filters.query.trim().toLowerCase();
    if (q) {
      posts = posts.filter(p => {
        const hay = `${p.title ?? ""} ${p.brand ?? ""} ${p.publicCode ?? ""} ${p.sourceUrl ?? ""}`.toLowerCase();
        return hay.includes(q);
      });
    }
    const counts = { all: posts.length, women: 0, men: 0, kids: 0, unisex: 0 };
    for (const p of posts) {
      if (p.gender && counts[p.gender] !== undefined) counts[p.gender]++;
    }
    return counts;
  }, [channelPosts, filters.brands, filters.priceMinDollars, filters.priceMaxDollars, filters.minDiscountPct, filters.query]);

  // ─── Search query logging for Pulse (migration 0043) ───
  // Debounced 1.2s after the last keystroke so we capture intent
  // ("user typed and paused to look at results"), not every
  // intermediate prefix. Best-effort: failures swallowed in
  // api.logSearch. Drives the 'demand gaps' Pulse widget.
  useEffect(() => {
    const q = (filters?.query || "").trim();
    if (!q || q.length < 2) return;  // ignore single chars / empty
    const timer = setTimeout(() => {
      api.logSearch({
        query: q,
        channelSlug: active?.slug,
        matchedCount: filteredPosts.length,
      });
    }, 1200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters?.query, active?.slug, filteredPosts.length]);

  // ─── Forward selection state (Phase 7 — reseller forwarding) ───
  // selectedPostIds is the manually-picked subset; selectionMode is the
  // global on/off (checkboxes show up only when on). The sticky bar
  // also offers "select all in current filter" so a reseller can ship
  // 200 cards at once without ticking each box.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedPostIds, setSelectedPostIds] = useState(() => new Set());
  const [forwardOpen, setForwardOpen] = useState(null); // null | { mode: 'selected' | 'all-filtered', postIds: string[] }
  const [historyOpen, setHistoryOpen] = useState(false);
  // Cross-channel BG code lookup. Opens a small modal that hits
  // GET /api/posts/by-code/:code so a reseller can find a card their
  // customer mentioned without having to remember which channel it
  // came from.
  const [lookupOpen, setLookupOpen] = useState(false);

  const toggleSelected = (postId) =>
    setSelectedPostIds((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) next.delete(postId); else next.add(postId);
      return next;
    });
  const clearSelection = () => {
    setSelectedPostIds(new Set());
    setSelectionMode(false);
  };
  const openForwardWithSelection = () => {
    setForwardOpen({
      mode: "selected",
      postIds: Array.from(selectedPostIds),
    });
  };
  const openForwardWithFiltered = () => {
    setForwardOpen({
      mode: "all-filtered",
      postIds: filteredPosts.map((p) => p.id),
    });
  };

  // Persimmon attention strip: count СП posts that close in the next 24h.
  const closingSoonCount = useMemo(() => {
    const now = Date.now();
    return (channelPosts ?? []).filter(p => {
      if (p.mode !== "group_buy" || p.lockedAt) return false;
      if (!p.deadlineAt) return false;
      const msLeft = p.deadlineAt * 1000 - now;
      return msLeft > 0 && msLeft < 24 * 3600 * 1000;
    }).length;
  }, [channelPosts]);

  return (
    <>
      {/* Header — paper-raised, ink text + serif title */}
      <div style={{
        padding: "0 16px",
        paddingTop: "env(safe-area-inset-top, 0px)",
        display: "flex", alignItems: "center", gap: 12, minHeight: 64,
        background: T_NEW.paperRaised,
        borderBottom: `1px solid ${T_NEW.ink12}`,
      }}>
        {isMobile && (
          /* Hamburger — consistent with Dashboard / MyStore mobile
             chrome. Same handler as the back arrow used to call
             (onBack → setMobSb(true)); we just unify the affordance
             across the three top-level surfaces. */
          <button onClick={onBack} aria-label={t("channelfeed.menu")}
                  style={{ background: "none", border: "none",
                           cursor: "pointer", padding: "6px 8px",
                           fontSize: 22, lineHeight: 1,
                           color: T_NEW.ink }}>
            ☰
          </button>
        )}
        <Av name={chName(active, lang)} size={36} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Serif size={T_NEW.displaySm} style={{ fontWeight: 400, letterSpacing: "-0.01em" }}>
            {chName(active, lang)}
          </Serif>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 2 }}>
            <Mono size={T_NEW.micro}>
              {t(plural(channelPosts.length, "channel.subtitle.posts"), { n: channelPosts.length })}
            </Mono>
            {cashbackPct > 0 && <StatusPill tone="bottle">{t("channel.cashbackBadge", { pct: cashbackPct })}</StatusPill>}
          </div>
        </div>
        {user.role !== "customer" && (
          <button onClick={onCreatePost}
                  style={{ background: T_NEW.ink, color: T_NEW.paperRaised, border: "none",
                           cursor: "pointer", padding: "8px 14px", borderRadius: T_NEW.r2,
                           display: "flex", alignItems: "center", gap: 6,
                           fontFamily: T_NEW.fontSans, fontSize: T_NEW.body, fontWeight: 600 }}
                  title={t("channelfeed.newPost.title")}>
            <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
            <span>{t("channelfeed.newPost")}</span>
          </button>
        )}
      </div>

      {/* Scroll body — no top padding so the sticky filter bar can pin flush
          under the header (a top padding would leave an uncovered band where
          cards peek through while scrolling). The hero below restores the
          initial top spacing. */}
      <div ref={feedScrollRef} onScroll={updateScrollNav}
           style={{ flex: 1, overflowY: "auto", padding: "0 14px 32px" }}>

        {/* Editorial date + "Подборка дня" — only show if there are posts.
            View-mode toggle (list/grid) sits next to the heading on the
            right, since it directly controls what's rendered below it. */}
        {channelPosts.length > 0 && (
          <div style={{ padding: "0 4px", marginTop: 20, marginBottom: 16 }}>
            <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>{formatToday(lang)}</Mono>
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
              <Serif as="h2" size={T_NEW.hero} style={{ lineHeight: 1.05 }}>
                {t("channelfeed.heroTitle").split("\n").map((line, i, arr) => (
                  <span key={i}>{line}{i < arr.length - 1 && <br/>}</span>
                ))}
              </Serif>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                {/* History — "where do I buy items I forwarded?". Opens
                    a modal with past forward batches and click-through
                    to each source URL. */}
                <button
                  type="button"
                  onClick={() => setHistoryOpen(true)}
                  style={{
                    background: "transparent",
                    color: T_NEW.ink,
                    border: `1px solid ${T_NEW.ink12}`,
                    borderRadius: T_NEW.r1,
                    padding: "6px 12px",
                    fontFamily: T_NEW.fontSans,
                    fontSize: T_NEW.meta,
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                  title={t("channelfeed.history.title")}
                >
                  {t("channelfeed.history")}
                </button>
                {/* Cross-channel BG code lookup. Independent of the
                    in-channel search bar below — that's scoped to the
                    currently-open channel only. This button hits the
                    by-code API endpoint and can land on a card in any
                    channel. Customer-role accounts only; staff don't
                    need it (they have the admin panel). */}
                {user.role === "customer" && (
                  <button
                    type="button"
                    onClick={() => setLookupOpen(true)}
                    style={{
                      background: "transparent",
                      color: T_NEW.ink,
                      border: `1px solid ${T_NEW.ink12}`,
                      borderRadius: T_NEW.r1,
                      padding: "6px 12px",
                      fontFamily: T_NEW.fontSans,
                      fontSize: T_NEW.meta,
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                    title={t("channelfeed.findByCode.title")}
                  >
                    {t("channelfeed.findByCode")}
                  </button>
                )}
                {/* Reseller forward mode toggle — flips checkboxes on
                    each card. When mode is on, header label changes so
                    it's obvious where the action lives.
                    Locked for non-approved resellers: forwarding leaks
                    the wholesale image (which contains the price) so
                    we refuse client-side AND server-side. */}
                {(() => {
                  const fwLocked = user.role === "customer"
                    && user.applicationStatus !== "approved";
                  return (
                    <button
                      type="button"
                      disabled={fwLocked}
                      onClick={() => {
                        if (fwLocked) return;
                        if (selectionMode) { clearSelection(); } else { setSelectionMode(true); }
                      }}
                      style={{
                        background: selectionMode ? T_NEW.ink : "transparent",
                        color: fwLocked ? T_NEW.ink38
                          : selectionMode ? T_NEW.paperRaised : T_NEW.ink,
                        border: `1px solid ${selectionMode ? T_NEW.ink : T_NEW.ink12}`,
                        borderRadius: T_NEW.r1,
                        padding: "6px 12px",
                        fontFamily: T_NEW.fontSans,
                        fontSize: T_NEW.meta,
                        cursor: fwLocked ? "not-allowed" : "pointer",
                        opacity: fwLocked ? 0.6 : 1,
                        fontWeight: 500,
                      }}
                      title={fwLocked
                        ? t("channelfeed.forward.locked.title")
                        : t("channelfeed.forward.toggle.title")}
                    >
                      {fwLocked
                        ? t("channelfeed.forward.locked.label")
                        : selectionMode
                          ? t("channelfeed.forward.toggle.on")
                          : t("channelfeed.forward.toggle.off")}
                    </button>
                  );
                })()}
                {/* Staff-only Archived view toggle — appears when the channel
                    has any archived posts (or while the view is open). */}
                {user.role !== "customer" && (archivedCount > 0 || showArchived) && (
                  <button type="button" onClick={onToggleArchived}
                    title={showArchived ? "Show active posts" : "Show archived posts"}
                    style={{
                      background: showArchived ? T_NEW.ink : "transparent",
                      color: showArchived ? T_NEW.paperRaised : T_NEW.ink,
                      border: `1px solid ${showArchived ? T_NEW.ink : T_NEW.ink12}`,
                      borderRadius: T_NEW.r1, padding: "6px 12px", cursor: "pointer",
                      fontFamily: T_NEW.fontSans, fontSize: T_NEW.meta, fontWeight: 500,
                    }}>
                    {showArchived
                      ? (lang === "ru" ? "← Активные" : lang === "es" ? "← Activos" : "← Active")
                      : (lang === "ru" ? `Архив (${archivedCount})`
                         : lang === "es" ? `Archivados (${archivedCount})`
                         : `Archived (${archivedCount})`)}
                  </button>
                )}
                <ViewToggle value={viewMode} onChange={setViewMode} t={t} />
              </div>
            </div>
            {closingSoonCount > 0 && (
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                background: T_NEW.persimmonSoft, color: T_NEW.persimmon,
                padding: "8px 12px", borderRadius: T_NEW.r1,
                fontFamily: T_NEW.fontSans, fontSize: T_NEW.meta, fontWeight: 600,
                marginTop: 12,
              }}>
                <span aria-hidden style={{ width: 6, height: 6, borderRadius: "50%", background: T_NEW.persimmon }} />
                <span>
                  {t(closingSoonCount === 1 ? "channelfeed.closingSoon.one" : "channelfeed.closingSoon.many", { n: closingSoonCount })}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Empty state */}
        {channelPosts.length === 0 && (
          <div style={{ textAlign: "center", padding: "48px 24px", maxWidth: 380, margin: "0 auto" }}>
            <svg width="48" height="36" viewBox="0 0 64 48" aria-hidden style={{ marginBottom: T_NEW.s4, color: T_NEW.persimmon }}>
              <rect x="2" y="6" width="60" height="36" rx="2" fill="none" stroke={T_NEW.ink} strokeWidth="1.5"/>
              <path d="M2 8 L32 28 L62 8" fill="none" stroke={T_NEW.ink} strokeWidth="1.5"/>
              <circle cx="50" cy="36" r="6" fill={T_NEW.persimmon}/>
            </svg>
            <Serif as="h2" size={T_NEW.displayMd} style={{ marginBottom: 8 }}>
              {t("empty.channelFeed.title")}
            </Serif>
            <p style={{ color: T_NEW.ink56, fontSize: T_NEW.body, lineHeight: 1.55 }}>
              {user.role === "customer"
                ? t("empty.channelFeed.customer")
                : t("channelfeed.empty.staff")}
            </p>
          </div>
        )}

        {/* Filter + sort bar — only when there are posts to filter. Pinned
            sticky to the top of the scroll body (right under the channel
            header) so search/filters stay reachable while scrolling a long
            feed. Negative side margin full-bleeds over the scroll body's
            14px padding; the paper background + hairline cover the cards
            sliding underneath. */}
        {channelPosts.length > 0 && (
          <div style={{
            position: "sticky", top: 0, zIndex: 6,
            background: T_NEW.paper,
            margin: "0 -14px", padding: "8px 14px 0",
            boxShadow: `0 1px 0 ${T_NEW.ink12}`,
          }}>
            <FilterBar
              t={t}
              brands={uniqueBrands}
              filters={filters}
              setFilters={setFilters}
              sortBy={sortBy}
              setSortBy={setSortBy}
              activeFilterCount={activeFilterCount}
              onClear={clearFilters}
              resultCount={filteredPosts.length}
              totalCount={channelPosts.length}
            />
          </div>
        )}

        {/* Gender filter chips (migration 0055). Only rendered when the
            channel has any gendered content — for a channel with all
            NULL gender (e.g. a Walmart category), the chip strip would
            just show "All N · Women 0 · Men 0" which is noise. */}
        {channelPosts.length > 0 &&
         (genderCounts.women + genderCounts.men + genderCounts.kids + genderCounts.unisex) > 0 && (
          <div style={{
            display: "flex", gap: 8, flexWrap: "wrap",
            marginBottom: 14, padding: "0 4px",
          }}>
            {[
              { key: "",       label: t("channelfeed.gender.all"),     count: genderCounts.all },
              { key: "women",  label: t("channelfeed.gender.women"),   count: genderCounts.women },
              { key: "men",    label: t("channelfeed.gender.men"),     count: genderCounts.men },
              { key: "kids",   label: t("channelfeed.gender.kids"),    count: genderCounts.kids },
              { key: "unisex", label: t("channelfeed.gender.unisex"),  count: genderCounts.unisex },
            ]
              // Hide gender chips with 0 in the current view (except "All",
              // always shown so the user can reset).
              .filter(c => c.key === "" || c.count > 0)
              .map(c => {
                const active = filters.gender === c.key;
                return (
                  <button key={c.key || "all"} type="button"
                    onClick={() => setFilters(f => ({ ...f, gender: c.key }))}
                    style={{
                      background: active ? T_NEW.accent : "transparent",
                      color: active ? "#fff" : T_NEW.accent,
                      border: `1px solid ${T_NEW.accent}`,
                      borderRadius: 999,
                      padding: "5px 12px",
                      fontSize: 13, fontWeight: 600,
                      fontFamily: T_NEW.fontSans,
                      cursor: "pointer",
                      display: "inline-flex", alignItems: "center", gap: 6,
                    }}>
                    {c.label}
                    <span style={{ opacity: 0.75, fontWeight: 500 }}>{c.count}</span>
                  </button>
                );
              })}
          </div>
        )}

        {/* Post cards — grid mode uses CSS Grid auto-fit so column
            count adapts to viewport (3 columns on a typical laptop,
            4-5 on a 4K monitor, 1 on a phone). List mode wraps the
            same PostCard nodes (they self-cap at maxWidth 600 inside
            their article). */}
        <div style={
          viewMode === "grid"
            ? {
                display: "grid",
                // A 300px min track only fits ONE column on a phone, so grid
                // mode looked identical to the list. Force 2 columns on mobile;
                // wider screens keep auto-filling 300px tracks (3-5 columns).
                gridTemplateColumns: isMobile
                  ? "repeat(2, minmax(0, 1fr))"
                  : "repeat(auto-fill, minmax(300px, 1fr))",
                gap: isMobile ? 10 : 16,
                alignItems: "start",
              }
            : undefined
        }>
          {filteredPosts.slice(0, visibleCount).map(p => (
            <PostCard
              key={p.id} p={p} t={t} lang={lang} toast={toast} user={user}
              view={viewMode}
              selectionMode={selectionMode}
              selected={selectedPostIds.has(p.id)}
              onToggleSelect={() => toggleSelected(p.id)}
              onOrderClick={() => setOrderingPost(p)}
              onOrderIntent={onOrderIntent}
              onAfterPostChange={onAfterPostChange}
              onOpenPremiumSettings={onOpenPremiumSettings}
            />
          ))}
          {/* Empty-after-filter state */}
          {filteredPosts.length === 0 && channelPosts.length > 0 && (
            <div style={{ gridColumn: "1 / -1", padding: "32px 12px", textAlign: "center", color: T_NEW.ink56 }}>
              <p style={{ marginBottom: 8 }}>{t("channelfeed.noMatch.title")}</p>
              <button onClick={clearFilters}
                      style={{ background: "none", border: "none", color: T_NEW.persimmon,
                               cursor: "pointer", fontFamily: T_NEW.fontMono, fontSize: T_NEW.meta,
                               textTransform: "uppercase", letterSpacing: T_NEW.monoTrack }}>
                {t("channelfeed.noMatch.clear")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Floating scroll-to-top / scroll-to-bottom for long feeds. Hidden in
          selection mode (the forward action bar owns the bottom edge). */}
      {!selectionMode && (showScrollUp || showScrollDown) && (
        <div style={{
          position: "fixed", right: 14,
          bottom: "calc(20px + env(safe-area-inset-bottom, 0px))",
          display: "flex", flexDirection: "column", gap: 8, zIndex: 19,
        }}>
          {showScrollUp && (
            <button type="button" onClick={() => scrollFeed("top")}
              aria-label={lang === "ru" ? "Наверх" : lang === "es" ? "Arriba" : "Scroll to top"}
              style={navBtnStyle}>↑</button>
          )}
          {showScrollDown && (
            <button type="button" onClick={() => scrollFeed("bottom")}
              aria-label={lang === "ru" ? "Вниз" : lang === "es" ? "Abajo" : "Scroll to bottom"}
              style={navBtnStyle}>↓</button>
          )}
        </div>
      )}

      {orderingPost && (
        <OrderModal
          post={orderingPost}
          onClose={() => setOrderingPost(null)}
          onDone={() => { onAfterPostChange?.(); }}
        />
      )}

      {/* ─── Sticky forward action bar — only when selection mode is on ─
          Pinned to the bottom of the viewport so it's reachable
          regardless of scroll position. Two CTAs:
            - "Переслать N выбранных" (disabled until at least 1 ticked)
            - "Переслать все NN по фильтру" (always available; lets you
              ship every result of the current filter set without
              hand-ticking each one). */}
      {selectionMode && (
        <div style={{
          position: "fixed",
          bottom: 0, left: 0, right: 0,
          background: T_NEW.paperRaised,
          borderTop: `1px solid ${T_NEW.ink12}`,
          boxShadow: "0 -2px 12px rgba(20, 17, 13, 0.06)",
          padding: "10px 14px",
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
          zIndex: 20,
        }}>
          <span style={{
            fontFamily: T_NEW.fontMono, fontSize: T_NEW.meta, color: T_NEW.ink56,
            textTransform: "uppercase", letterSpacing: T_NEW.monoTrack,
          }}>
            {t("channelfeed.selectedShort", { n: selectedPostIds.size })}
          </span>
          <Btn
            variant="accent"
            disabled={selectedPostIds.size === 0}
            onClick={openForwardWithSelection}
          >
            {t("channelfeed.forwardSelected")}
          </Btn>
          <Btn
            variant="outline"
            onClick={openForwardWithFiltered}
            disabled={filteredPosts.length === 0}
          >
            {t("channelfeed.forwardFilteredN", { n: filteredPosts.length })}
          </Btn>
          <Btn variant="ghost" onClick={clearSelection}>
            {t("channelfeed.cancelSelect")}
          </Btn>
        </div>
      )}

      {forwardOpen && (
        <ForwardModal
          mode={forwardOpen.mode}
          postIds={forwardOpen.postIds}
          posts={filteredPosts}
          onClose={() => setForwardOpen(null)}
          onSent={(r) => {
            // Don't close here — ForwardModal stays open and shows the
            // per-post ResultSummary (including any failures), closing
            // itself via its Done button. Only show a success toast on a
            // fully clean run; a partial/total failure must NOT read as
            // "Переслано", which previously masked dropped cards.
            const ok = r?.okCount ?? 0;
            const total = r?.totalCount ?? 0;
            if (r?.status !== "failed" && total > 0 && ok === total) {
              clearSelection();
              toast.success(t("channelfeed.toast.forwardSent"));
            }
          }}
          onOpenForwardSettings={onOpenForwardSettings}
          toast={toast}
        />
      )}

      {historyOpen && (
        <ForwardHistoryModal
          onClose={() => setHistoryOpen(false)}
          toast={toast}
        />
      )}

      {lookupOpen && (
        <CodeLookupModal
          onClose={() => setLookupOpen(false)}
          onJumpToChannel={onJumpToChannel}
          onOpenPremiumSettings={onOpenPremiumSettings}
        />
      )}
    </>
  );
}

// ─── ViewToggle ─────────────────────────────────────────────────────
// Tiny segmented control (list | grid) that sits next to the "Подборка
// дня" heading. Two inline SVG icons in a single rounded pill — active
// state in persimmon to match the brand accent. Keyboard-focusable.
function ViewToggle({ value, onChange, t }) {
  const Item = ({ mode, label, children }) => {
    const active = value === mode;
    return (
      <button
        type="button"
        onClick={() => onChange(mode)}
        aria-pressed={active}
        aria-label={label}
        title={label}
        style={{
          background: active ? T_NEW.ink : "transparent",
          color: active ? T_NEW.paperRaised : T_NEW.ink56,
          border: "none",
          cursor: "pointer",
          padding: "6px 10px",
          borderRadius: T_NEW.r1,
          display: "flex",
          alignItems: "center",
          transition: "background 0.12s, color 0.12s",
        }}
      >
        {children}
      </button>
    );
  };
  return (
    <div style={{
      display: "inline-flex",
      background: T_NEW.paperSoft,
      border: `1px solid ${T_NEW.ink12}`,
      borderRadius: T_NEW.r1,
      padding: 2,
      gap: 2,
      alignSelf: "flex-end",
      marginBottom: 4,
    }}>
      <Item mode="list" label={t("channelfeed.view.list")}>
        {/* 3 horizontal rows */}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
          <rect x="2" y="3"  width="12" height="2" rx="0.5" fill="currentColor"/>
          <rect x="2" y="7"  width="12" height="2" rx="0.5" fill="currentColor"/>
          <rect x="2" y="11" width="12" height="2" rx="0.5" fill="currentColor"/>
        </svg>
      </Item>
      <Item mode="grid" label={t("channelfeed.view.grid")}>
        {/* 2x2 grid */}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
          <rect x="2" y="2" width="5" height="5" rx="0.5" fill="currentColor"/>
          <rect x="9" y="2" width="5" height="5" rx="0.5" fill="currentColor"/>
          <rect x="2" y="9" width="5" height="5" rx="0.5" fill="currentColor"/>
          <rect x="9" y="9" width="5" height="5" rx="0.5" fill="currentColor"/>
        </svg>
      </Item>
    </div>
  );
}


// ─── FilterBar ──────────────────────────────────────────────────────
// Single-row (wraps on narrow viewports) control band above the post
// grid. Houses: brand multi-select dropdown, $min/$max number inputs,
// minimum-discount preset chips, sort dropdown, and a result count.
// Designed so the most-used filter (discount %) is one click away.
function FilterBar({ t, brands, filters, setFilters, sortBy, setSortBy, activeFilterCount, onClear, resultCount, totalCount }) {
  const [brandOpen, setBrandOpen] = useState(false);

  const setMinPrice = (v) => setFilters(f => ({ ...f, priceMinDollars: v.replace(/[^\d]/g, "") }));
  const setMaxPrice = (v) => setFilters(f => ({ ...f, priceMaxDollars: v.replace(/[^\d]/g, "") }));
  const setMinDiscount = (n) => setFilters(f => ({ ...f, minDiscountPct: n }));
  const toggleBrand = (b) => setFilters(f => {
    const s = new Set(f.brands);
    if (s.has(b)) s.delete(b); else s.add(b);
    return { ...f, brands: s };
  });

  const chipBase = {
    background: T_NEW.paperRaised,
    border: `1px solid ${T_NEW.ink12}`,
    color: T_NEW.ink,
    padding: "6px 10px",
    borderRadius: T_NEW.r1,
    fontFamily: T_NEW.fontSans,
    fontSize: T_NEW.meta,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  };
  const chipActive = { ...chipBase, background: T_NEW.ink, color: T_NEW.paperRaised, borderColor: T_NEW.ink };

  return (
    <div style={{ padding: "0 4px", marginBottom: 14 }}>
      {/* Row 1 — keyword search (flexes to fill) + brand + sort. */}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {/* Keyword search — highest-utility filter; matches title + brand +
            URL (SKU on scraped posts). Flexes to fill the row. */}
        <div style={{ position: "relative", display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
          <span aria-hidden style={{
            position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)",
            color: T_NEW.ink56, pointerEvents: "none",
          }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="6" cy="6" r="4.25" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M9.5 9.5 L12.5 12.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          </span>
          <input
            type="text"
            placeholder={t("channelfeed.search.placeholder")}
            value={filters.query}
            onChange={(e) => setFilters(f => ({ ...f, query: e.target.value }))}
            style={{
              width: "100%", padding: "6px 26px 6px 28px", boxSizing: "border-box",
              border: `1px solid ${T_NEW.ink12}`, borderRadius: T_NEW.r1,
              fontFamily: T_NEW.fontSans, fontSize: T_NEW.meta,
              background: T_NEW.paperRaised, color: T_NEW.ink,
            }}
          />
          {filters.query && (
            <button
              type="button"
              onClick={() => setFilters(f => ({ ...f, query: "" }))}
              aria-label={t("channelfeed.search.clear")}
              style={{
                position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                background: "none", border: "none", cursor: "pointer",
                color: T_NEW.ink56, padding: 2, lineHeight: 1,
              }}
            >×</button>
          )}
        </div>

        {/* Brand filter */}
        <div style={{ position: "relative", flex: "0 0 auto" }}>
          <button
            type="button"
            onClick={() => setBrandOpen(o => !o)}
            style={filters.brands.size > 0 ? chipActive : chipBase}
          >
            {filters.brands.size > 0
              ? t("channelfeed.filterBar.brand.count", { n: filters.brands.size })
              : t("channelfeed.filterBar.brand")}
            <span style={{ fontSize: 10, opacity: 0.7 }}>▾</span>
          </button>
          {brandOpen && (
            <>
              {/* click-outside backdrop */}
              <div onClick={() => setBrandOpen(false)}
                   style={{ position: "fixed", inset: 0, zIndex: 10 }} />
              <div style={{
                position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 11,
                background: T_NEW.paperRaised, border: `1px solid ${T_NEW.ink12}`,
                borderRadius: T_NEW.r2, boxShadow: T_NEW.shadowCard,
                padding: 8, maxHeight: 320, overflowY: "auto", minWidth: 220,
              }}>
                {brands.length === 0 && (
                  <div style={{ padding: 8, color: T_NEW.ink56, fontSize: T_NEW.meta }}>{t("channelfeed.filterBar.brand.empty")}</div>
                )}
                {brands.map(b => (
                  <label key={b} style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "6px 8px", borderRadius: T_NEW.r1, cursor: "pointer",
                    fontSize: T_NEW.meta,
                  }}>
                    <input type="checkbox"
                           checked={filters.brands.has(b)}
                           onChange={() => toggleBrand(b)} />
                    <span>{b}</span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Sort — native select; the SORT label is dropped to keep the row
            tight (the selected value is self-describing). */}
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                aria-label={t("channelfeed.filterBar.sort")}
                style={{
                  flex: "0 0 auto", maxWidth: 132,
                  padding: "6px 8px",
                  border: `1px solid ${T_NEW.ink12}`,
                  borderRadius: T_NEW.r1,
                  background: T_NEW.paperRaised,
                  color: T_NEW.ink,
                  fontFamily: T_NEW.fontSans,
                  fontSize: T_NEW.meta,
                  cursor: "pointer",
                }}>
          <option value="newest">{t("channelfeed.filterBar.sort.newest")}</option>
          <option value="discount-desc">{t("channelfeed.filterBar.sort.discountDesc")}</option>
          <option value="price-asc">{t("channelfeed.filterBar.sort.priceAsc")}</option>
          <option value="price-desc">{t("channelfeed.filterBar.sort.priceDesc")}</option>
          <option value="brand">{t("channelfeed.filterBar.sort.brand")}</option>
        </select>
      </div>

      {/* Row 2 — price range + min-discount chips. Horizontal-scrolls if it
          can't fit, so the bar never spills onto a third row. */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8,
                    flexWrap: "nowrap", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        {/* Price filter — two compact $ inputs */}
        <div style={{ display: "inline-flex", alignItems: "center", gap: 4, flex: "0 0 auto" }}>
          <span style={{ fontFamily: T_NEW.fontMono, fontSize: T_NEW.meta, color: T_NEW.ink56 }}>$</span>
          <input
            type="text" inputMode="numeric" placeholder={t("channelfeed.filterBar.priceMin")}
            value={filters.priceMinDollars}
            onChange={(e) => setMinPrice(e.target.value)}
            style={{ width: 54, padding: "5px 8px", border: `1px solid ${T_NEW.ink12}`,
                     borderRadius: T_NEW.r1, fontFamily: T_NEW.fontSans, fontSize: T_NEW.meta,
                     background: T_NEW.paperRaised, color: T_NEW.ink }}
          />
          <span style={{ color: T_NEW.ink38 }}>—</span>
          <span style={{ fontFamily: T_NEW.fontMono, fontSize: T_NEW.meta, color: T_NEW.ink56 }}>$</span>
          <input
            type="text" inputMode="numeric" placeholder={t("channelfeed.filterBar.priceMax")}
            value={filters.priceMaxDollars}
            onChange={(e) => setMaxPrice(e.target.value)}
            style={{ width: 54, padding: "5px 8px", border: `1px solid ${T_NEW.ink12}`,
                     borderRadius: T_NEW.r1, fontFamily: T_NEW.fontSans, fontSize: T_NEW.meta,
                     background: T_NEW.paperRaised, color: T_NEW.ink }}
          />
        </div>

        {/* Min-discount — a dropdown (the 4 preset chips overflowed the row on
            mobile). Goes dark when a threshold is active, like the old chips. */}
        <select value={filters.minDiscountPct}
                onChange={(e) => setMinDiscount(Number(e.target.value))}
                aria-label={t("channelfeed.filterBar.anyDiscount")}
                style={{
                  flex: "0 0 auto",
                  padding: "6px 8px",
                  border: `1px solid ${filters.minDiscountPct > 0 ? T_NEW.ink : T_NEW.ink12}`,
                  borderRadius: T_NEW.r1,
                  background: filters.minDiscountPct > 0 ? T_NEW.ink : T_NEW.paperRaised,
                  color: filters.minDiscountPct > 0 ? T_NEW.paperRaised : T_NEW.ink,
                  fontFamily: T_NEW.fontSans, fontSize: T_NEW.meta, cursor: "pointer",
                }}>
          <option value={0}>{t("channelfeed.filterBar.anyDiscount")}</option>
          <option value={25}>{t("channelfeed.filterBar.fromPct", { n: 25 })}</option>
          <option value={50}>{t("channelfeed.filterBar.fromPct", { n: 50 })}</option>
          <option value={75}>{t("channelfeed.filterBar.fromPct", { n: 75 })}</option>
        </select>
      </div>

      {/* Result count + clear-filters link — only when filters active */}
      {activeFilterCount > 0 && (
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro, color: T_NEW.ink56,
                         textTransform: "uppercase", letterSpacing: T_NEW.monoTrack }}>
            {t("channelfeed.filterBar.resultOf", { n: resultCount, total: totalCount })}
          </span>
          <button onClick={onClear}
                  style={{ background: "none", border: "none", cursor: "pointer",
                           color: T_NEW.persimmon, fontFamily: T_NEW.fontMono,
                           fontSize: T_NEW.micro, textTransform: "uppercase",
                           letterSpacing: T_NEW.monoTrack }}>
            {t("channelfeed.filterBar.clear")}
          </button>
        </div>
      )}
    </div>
  );
}


// ─── PostCard ───────────────────────────────────────────────────────
function PostCard({
  p, t, lang, toast, user, view = "list",
  selectionMode = false, selected = false, onToggleSelect,
  onOrderClick, onOrderIntent, onAfterPostChange,
  // Used by the "Источник в Premium" pill — taps land on the Premium
  // upgrade pitch in SettingsModal (migration 0038 wiring).
  onOpenPremiumSettings,
}) {
  const gbLocked = p.mode === "group_buy" && !!p.lockedAt;
  const isStaff = user.role !== "customer";
  const isGrid = view === "grid";
  // Application gate (migration 0052). Wholesale prices are baked
  // into the rendered card image, so the only safe lock for a
  // non-approved reseller is to swap the image for a placeholder.
  // Staff are always approved; this only affects customer-role
  // accounts that haven't passed the application gate yet.
  const priceLocked = !isStaff && user.applicationStatus !== "approved";

  // Staff "assign gender" for the Costco review bucket (migration 0067):
  // move a genderless item into Men's/Women's/Kids'. The feed refresh
  // (onAfterPostChange) drops the card from the review channel.
  const [moving, setMoving] = useState(false);
  // Lightbox: index of the image being viewed full-screen, or null.
  const [lightbox, setLightbox] = useState(null);
  const imgs = p.images || [];
  async function moveToGender(slug) {
    if (moving) return;
    setMoving(true);
    try {
      await api.patchPost(p.id, { channelSlug: slug });
      toast.success(t("review.moved"));
      onAfterPostChange?.();
    } catch (e) {
      toast.error(e.message || String(e));
      setMoving(false);
    }
  }

  return (
    <article
      onClick={(e) => {
        // In selection mode, clicking anywhere on the card toggles it.
        // Buttons and links inside the card stop propagation so they
        // still work normally; the article-level handler is the
        // fallback "big touch target" for fast multi-select.
        if (selectionMode && onToggleSelect) onToggleSelect();
      }}
      style={{
      background: T_NEW.paperRaised,
      borderRadius: T_NEW.r3,
      position: "relative",
      // In list view we cap width and center for editorial legibility.
      // In grid view we fill the grid cell (which is already sized by
      // the parent CSS Grid), so let the article be 100% wide.
      ...(isGrid
        ? { width: "100%", marginBottom: 0 }
        : { maxWidth: 600, marginLeft: "auto", marginRight: "auto", marginBottom: 16 }),
      overflow: "hidden",
      border: `1px solid ${selectionMode && selected ? T_NEW.persimmon : T_NEW.ink12}`,
      boxShadow: selectionMode && selected
        ? `0 0 0 2px ${T_NEW.persimmonSoft}, ${T_NEW.shadowCard}`
        : T_NEW.shadowCard,
      cursor: selectionMode ? "pointer" : "default",
      animation: "fadeUp 0.18s",
    }}>
      {/* Image lightbox — tap any photo to enlarge. Portaled to <body> so the
          card's overflow:hidden + transforms don't clip the full-screen view.
          Tap backdrop or × to close; ‹ › cycle when there are multiple. */}
      {lightbox != null && imgs[lightbox] && createPortal(
        <div onClick={() => setLightbox(null)}
             style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.92)",
                      display: "flex", flexDirection: "column", alignItems: "center",
                      justifyContent: "center", gap: 16, padding: 16 }}>
          <img src={imgs[lightbox].url} alt="" onClick={(e) => e.stopPropagation()}
               style={{ maxWidth: "100%", maxHeight: "82vh", objectFit: "contain", borderRadius: 8 }} />
          {imgs.length > 1 && (
            <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <button onClick={() => setLightbox((lightbox - 1 + imgs.length) % imgs.length)}
                      style={{ minWidth: 44, minHeight: 44, borderRadius: 22, border: "none",
                               background: "rgba(255,255,255,0.15)", color: "#fff", fontSize: 22, cursor: "pointer" }}>‹</button>
              <Mono size={12} color="rgba(255,255,255,0.85)">{lightbox + 1} / {imgs.length}</Mono>
              <button onClick={() => setLightbox((lightbox + 1) % imgs.length)}
                      style={{ minWidth: 44, minHeight: 44, borderRadius: 22, border: "none",
                               background: "rgba(255,255,255,0.15)", color: "#fff", fontSize: 22, cursor: "pointer" }}>›</button>
            </div>
          )}
          <button onClick={() => setLightbox(null)} aria-label="Close"
                  style={{ position: "fixed", top: 14, right: 16, width: 44, height: 44, borderRadius: 22,
                           border: "none", background: "rgba(255,255,255,0.15)", color: "#fff",
                           fontSize: 26, lineHeight: 1, cursor: "pointer" }}>×</button>
        </div>,
        document.body,
      )}
      {/* Staff: assign a gender to a genderless Costco review-bucket item
          (migration 0067), moving it into the right channel. Hidden for
          customers (who can't see the is_internal review bucket anyway). */}
      {isStaff && p.channel?.slug === "costco" && (
        <div onClick={(e) => e.stopPropagation()}
             style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8,
                      padding: "8px 12px", background: T_NEW.persimmonSoft,
                      borderBottom: `1px solid ${T_NEW.ink12}` }}>
          <Mono size={T_NEW.micro} color={T_NEW.ink}>{t("review.assignGender")}</Mono>
          {[["costco-mens", t("review.mens")],
            ["costco-womens", t("review.womens")],
            ["costco-kids", t("review.kids")]].map(([slug, label]) => (
            <button key={slug} disabled={moving} onClick={() => moveToGender(slug)}
              style={{ minHeight: 32, padding: "4px 12px", borderRadius: 16,
                       cursor: moving ? "default" : "pointer", opacity: moving ? 0.5 : 1,
                       border: `1px solid ${T_NEW.persimmon}`, background: "transparent",
                       color: T_NEW.persimmon, fontFamily: T_NEW.fontMono,
                       fontSize: T_NEW.micro, fontWeight: 600 }}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Selection checkbox — only visible in selection mode. Sits
          top-left, over the image area. Pointer events on the box
          stop propagation so the article-level onClick doesn't
          double-fire. */}
      {selectionMode && (
        <div
          onClick={(e) => { e.stopPropagation(); onToggleSelect?.(); }}
          style={{
            position: "absolute",
            top: 10, left: 10,
            zIndex: 5,
            width: 28, height: 28,
            borderRadius: T_NEW.r1,
            background: selected ? T_NEW.persimmon : "rgba(244, 239, 231, 0.95)",
            border: `2px solid ${selected ? T_NEW.persimmon : T_NEW.ink38}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
            transition: "background 0.12s, border-color 0.12s",
          }}
          aria-checked={selected}
          role="checkbox"
        >
          {selected && (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M3 8 L7 12 L13 4" stroke={T_NEW.paperRaised} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
      )}
      {/* Hero image — show full square aspect ratio (no cropping).
          The scraped product cards are 1:1; the publish pipeline relies
          on the BRAND/title/price text being visible BELOW the image
          grid, so cropping with objectFit:"cover" + maxHeight cuts off
          the editorial copy. width:100% + height:auto lets the image
          scale proportionally to whatever the post card's width is. */}
      {p.images?.[0] && !priceLocked && (
        <div style={{ background: T_NEW.paperSoft }}>
          {/* Multi-photo posts (Walmart/warehouse) render a 2×2 collage of
              up to 4 product photos — the original card look, but each tile
              is a real image you can tap to enlarge. Single-image posts (the
              baked NR cards) keep the full-bleed hero. lazy/async loading
              keeps a 100+ post channel from firing every request at once on
              the no-CDN VPS. */}
          {imgs.length > 1 ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              {imgs.slice(0, 4).map((img, i) => (
                <img key={img.id} src={thumbUrl(img.url)} alt=""
                     loading="lazy" decoding="async"
                     onClick={(e) => { if (selectionMode) return; e.stopPropagation(); setLightbox(i); }}
                     style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover",
                              display: "block", cursor: "zoom-in" }} />
              ))}
            </div>
          ) : (
            <img src={thumbUrl(p.images[0].url)} alt=""
                 loading="lazy" decoding="async"
                 onClick={(e) => { if (selectionMode) return; e.stopPropagation(); setLightbox(0); }}
                 style={{ width: "100%", height: "auto", display: "block", cursor: "zoom-in" }} />
          )}
        </div>
      )}
      {/* Locked placeholder — shown to non-approved resellers in place
          of the rendered card image (which has the wholesale price
          baked in). Brand + title still rendered as text below in the
          body block since those don't leak pricing. */}
      {p.images?.[0] && priceLocked && (
        <div style={{ background: T_NEW.paperSoft,
                      aspectRatio: "1 / 1",
                      display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "center",
                      gap: 12, padding: 24, textAlign: "center" }}>
          <div style={{ fontSize: 36, opacity: 0.4 }}>🔒</div>
          <Mono size={13} color={T_NEW.ink56} style={{ fontWeight: 600 }}>
            {t("channelfeed.priceLocked")}
          </Mono>
          <div style={{ fontSize: 12, color: T_NEW.ink56,
                        fontFamily: T_NEW.fontSerif, fontStyle: "italic",
                        lineHeight: 1.5, maxWidth: 280 }}>
            {t("channelfeed.priceLocked.body")}
          </div>
        </div>
      )}
      {/* (Secondary-thumbnail strip removed — the 2×2 collage above now shows
          the extra photos; the lightbox cycles through all of them.) */}

      {/* Body */}
      <div style={{ padding: isGrid ? "10px 12px 12px" : "14px 16px 16px" }}>
        {/* When a post carries an image, the BuyerGram pipeline embeds
            brand + title + price + description directly into the card
            image (Plex Serif typography on cream background). Repeating
            the same fields below the image looks duplicated and breaks
            the Telegram-style "image is the post" aesthetic. So:
              - posts WITH an image: show only source line, price block
                (ruble conversion is a genuine add since the image only
                has USD), and the action row;
              - posts WITHOUT an image: show the full structured layout
                so user-typed posts still read clearly.
            Grid mode further drops the source/age line to keep cells
            visually quiet at small sizes. */}
        {(() => {
          const hasImage = !!p.images?.[0];
          // "Photo + live text" posts (Walmart, warehouse — migration 0071)
          // carry a plain product photo, so we render title/price/description
          // as localized text over it. Baked-card posts (NR fashion) keep
          // text hidden because it's already in the image pixels.
          const showText = !hasImage || p.showText;
          const locTitle =
            (lang === "es" ? (p.titleEs || p.titleEn || p.titleRu)
             : lang === "ru" ? (p.titleRu || p.titleEn || p.titleEs)
             : (p.titleEn || p.titleRu || p.titleEs)) || p.title;

          return (
            <>
              {/* Brand · Age — shown in BOTH list and grid views.
                  Category is NOT rendered here even though it's on the
                  post: scraper-set category strings ("walmart",
                  "new-markdowns", etc.) leak the upstream retailer to
                  the reseller's downstream customers. The channel
                  itself already communicates the broad product type
                  ("Витамины и БАДы" / "Личная гигиена" / etc.), so the
                  category field is redundant for customer-facing
                  display anyway. Category stays in the DB for admin
                  filtering. */}
              {(p.brand || p.publishedAt) && (
                <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
                  {p.brand}
                  {p.publishedAt && <span style={{ marginLeft: p.brand ? 8 : 0 }}>
                    {p.brand && " · "}{humanAge(p.publishedAt, t, lang)}
                  </span>}
                </Mono>
              )}

              {/* Title — Serif. Shown as live localized text for photo+text
                  posts; hidden for baked cards (image already shows it). */}
              {showText && !isBlankText(locTitle) && (
                <Serif as="h3" size={T_NEW.displaySm}
                       style={{ marginBottom: 8, fontWeight: 400, letterSpacing: "-0.005em", lineHeight: 1.2 }}>
                  {locTitle}
                </Serif>
              )}

              {/* Price block — hidden when an image is present because the
                  BuyerGram card already shows sale price, MSRP strikethrough,
                  and discount pill embedded in the image itself. Rendering
                  these fields again below the image was pure duplication.
                  ₽ conversion was the one piece of info the image couldn't
                  show, but that's now removed for bulk-customer-only Phase 0
                  (resellers apply their own markup downstream). Falls back
                  to showing the price for posts without an image so manual
                  text-only posts still read clearly. */}
              {showText && p.priceCents != null && !priceLocked && (
                <div style={{ display: "flex", alignItems: "baseline", gap: isGrid ? 6 : 10, flexWrap: "wrap", marginBottom: 10 }}>
                  {/* Large persimmon price tag under the photo. */}
                  <Serif size={isGrid ? T_NEW.displayMd : T_NEW.displayLg} color={T_NEW.persimmon}
                         style={{ fontWeight: 600, letterSpacing: "-0.01em" }}>
                    ${(p.priceCents / 100).toFixed(2)}
                  </Serif>
                  {p.originalPriceCents > 0 && (
                    <span style={{ color: T_NEW.ink38, fontSize: T_NEW.meta, textDecoration: "line-through" }}>
                      ${(p.originalPriceCents / 100).toFixed(2)}
                    </span>
                  )}
                  {p.discountPct ? <StatusPill tone="persimmon">−{p.discountPct}%</StatusPill> : null}
                </div>
              )}
              {showText && p.priceCents != null && priceLocked && (
                <div style={{ marginBottom: 10 }}>
                  <Mono size={12} color={T_NEW.ink56}>{t("channelfeed.priceLocked.full")}</Mono>
                </div>
              )}

              {/* Sale-price expiry (Costco promos). Always shown when set —
                  it's never embedded in the card image — so customers see the
                  deal's end date regardless of whether the card has a photo. */}
              {p.saleValidThrough && !priceLocked && (
                <div style={{ marginBottom: 10 }}>
                  <StatusPill tone="persimmon">
                    {t("card.saleEnds", { date: fmtShortDate(p.saleValidThrough, lang) })}
                  </StatusPill>
                </div>
              )}

              {/* Body copy — hidden when the image already contains it.
                  Show the viewer's language (es → bodyEs), falling back to
                  the other when one is missing (migration 0068). */}
              {!isGrid && showText && !isBlankText(lang === "es" ? (p.bodyEs || p.bodyEn || p.bodyRu) : lang === "ru" ? (p.bodyRu || p.bodyEn || p.bodyEs) : (p.bodyEn || p.bodyRu || p.bodyEs)) && (
                <p style={{ fontSize: T_NEW.body, color: T_NEW.ink, lineHeight: 1.55, whiteSpace: "pre-wrap", marginBottom: 12 }}>
                  {lang === "es" ? (p.bodyEs || p.bodyEn || p.bodyRu) : lang === "ru" ? (p.bodyRu || p.bodyEn || p.bodyEs) : (p.bodyEn || p.bodyRu || p.bodyEs)}
                </p>
              )}
            </>
          );
        })()}

        {/* СП block — ProgressArc + countdown */}
        {p.mode === "group_buy" && <GroupBuyBlock post={p} t={t} />}

        {/* Action row */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
          {p.status === "published" && p.priceCents != null && user.role === "customer" && (
            <Btn disabled={gbLocked}
                 variant="accent"
                 onClick={() => { if (!gbLocked) onOrderClick(); }}>
              {gbLocked ? t("post.locked") : t("post.order")}
            </Btn>
          )}
          {p.status === "published" && p.priceCents != null && user.role === "customer" && !gbLocked && (
            <Btn variant="outline"
                 onClick={() => { cartAdd(p); toast?.success?.(t("cart.added")); }}>
              {t("cart.add")}
            </Btn>
          )}
          {p.status === "published" && (p.priceCents == null || user.role !== "customer") && (
            <Btn variant="outline"
                 onClick={async () => {
                   try {
                     const r = await api.orderIntent(p.id);
                     onOrderIntent?.(r.dmId);
                   } catch (e) { toast.error(e.message); }
                 }}>
              {t("post.iWantThis")}
            </Btn>
          )}
          {/* Cashback link button removed — cashback infrastructure
              not yet wired (per ops note 2026-06-02). The API endpoint
              api.createCashbackLink stays in place so we can re-enable
              this button when the cashback aggregator is live without
              another UI deploy. */}
          {/* Customer-facing BG code (migration 0038). Always visible
              so resellers + their downstream customers can refer to a
              specific item without leaking the upstream retailer SKU.
              Click → copy to clipboard for convenience. */}
          {p.publicCode && (
            <span
              onClick={() => {
                navigator.clipboard?.writeText(p.publicCode);
                toast?.info?.(t("channelfeed.code.copied"));
              }}
              title={t("channelfeed.code.copyTitle")}
              style={{ color: T_NEW.persimmon, fontSize: T_NEW.meta,
                       fontFamily: T_NEW.fontMono, textTransform: "uppercase",
                       letterSpacing: T_NEW.monoTrack, fontWeight: 700,
                       cursor: "pointer", userSelect: "all" }}>
              {p.publicCode}
            </span>
          )}
          {/* Direct link to the upstream retailer page. Three states:
                1. sourceUrl present       → render the link
                2. no sourceUrl, no upstream → render nothing
                                              (warehouse-sourced posts,
                                              manual posts, manager
                                              WhatsApp content — there
                                              IS no upstream URL to gate)
                3. no sourceUrl, upstream    → render "Источник в Premium"
                   exists but is gated         upgrade pill
              The distinction matters because the upgrade pill misleads
              when there's nothing on the other side to unlock. Posts
              with `source` prefixed `warehouse:`, `manual:`, or
              `whatsapp:` are operator-created — premium can't reveal a
              source link that was never recorded. */}
          {(() => {
            if (p.sourceUrl) {
              return (
                <a href={p.sourceUrl} target="_blank" rel="noopener noreferrer"
                   style={{ color: T_NEW.ink56, fontSize: T_NEW.meta, textDecoration: "none",
                            fontFamily: T_NEW.fontMono, textTransform: "uppercase", letterSpacing: T_NEW.monoTrack }}>
                  {t("post.directLink")} ↗
                </a>
              );
            }
            // Suppress the premium-upgrade pill when the post is
            // explicitly operator-created — there's no upstream link
            // sitting in the DB that premium could reveal.
            const src = (p.source || "").toLowerCase();
            const isOperatorSourced =
              src.startsWith("warehouse:") ||
              src.startsWith("manual:") ||
              src.startsWith("whatsapp:");
            if (isOperatorSourced) return null;
            // Premium upsell removed — no "Источник в Premium" teaser pill.
            return null;
          })()}
        </div>

        {/* Per-color variant matrix (migration 0057). Renders only in
            list view and only when the post has 2+ colors. Lets the
            reseller answer "do you have Black in size 8?" without
            opening NR — preserves the source-anonymization promise
            even on follow-up questions. */}
        {!isGrid && Array.isArray(p.variants) && p.variants.length >= 2 && (
          <VariantsPanel variants={p.variants} t={t} />
        )}

        {/* Staff row — list view only. Hidden in grid to keep cells
            visually quiet at small sizes; admins can still switch to
            list view for edit/delete/audit actions. */}
        {!isGrid && isStaff && <StaffRow p={p} t={t} toast={toast} onAfterPostChange={onAfterPostChange} />}
      </div>
    </article>
  );
}

// ─── VariantsPanel — per-color size + price + stock visibility ──────
// Renders below the action row on multi-variant fashion posts. Color
// chips along the top; clicking one swaps the detail panel to that
// color's sizes-in-stock + price spread.
//
// Sold-out colors are visually dimmed and unclickable — the chip stays
// in place so the reseller can still tell the customer "Black is
// available, Briddle Brown is sold out across all sizes" without
// hunting for the missing color.
//
// Low-stock callout (≤ 5 units shipQuantity for the selected color)
// renders a persimmon "Only N left" hint so the reseller can warn the
// customer the listing is about to clear out.
function VariantsPanel({ variants, t }) {
  const initialIdx = useMemo(() => {
    const sel = variants.findIndex(v => v.is_selected && (v.sizes_in_stock?.length ?? 0) > 0);
    if (sel >= 0) return sel;
    const firstInStock = variants.findIndex(v => (v.sizes_in_stock?.length ?? 0) > 0);
    return firstInStock >= 0 ? firstInStock : 0;
  }, [variants]);
  const [activeIdx, setActiveIdx] = useState(initialIdx);
  const active = variants[activeIdx];

  const titleKey = plural(variants.length, "post.variants.title");
  const priceRange = (() => {
    if (!active || !active.price_cents_min) return null;
    const lo = active.price_cents_min / 100;
    const hi = (active.price_cents_max || active.price_cents_min) / 100;
    if (lo === hi) return `$${lo.toFixed(2)}`;
    return `$${lo.toFixed(2)} – $${hi.toFixed(2)}`;
  })();
  const showMsrp = active?.msrp_cents && active.price_cents_min &&
                   active.msrp_cents > active.price_cents_min;

  const activeSizes = active?.sizes_in_stock || [];
  const lowStock = active?.ship_quantity > 0 && active.ship_quantity <= 5;

  return (
    <div style={{
      marginTop: 14,
      paddingTop: 12,
      borderTop: `1px dashed ${T_NEW.ink38}`,
    }}>
      <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 8 }}>
        {t(titleKey, { n: variants.length })}
      </Mono>

      {/* Color chips */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
        {variants.map((v, i) => {
          const isActive = i === activeIdx;
          const isSoldOut = (v.sizes_in_stock?.length ?? 0) === 0;
          return (
            <button
              key={`${v.name}-${i}`}
              onClick={() => !isSoldOut && setActiveIdx(i)}
              disabled={isSoldOut}
              title={isSoldOut ? t("post.variants.soldOut") : v.color_family || v.name}
              style={{
                padding: "4px 10px",
                borderRadius: 14,
                border: `1px solid ${isActive ? T_NEW.persimmon : T_NEW.ink38}`,
                background: isActive ? T_NEW.persimmon : "transparent",
                color: isActive ? "#fff" : (isSoldOut ? T_NEW.ink38 : T_NEW.ink),
                cursor: isSoldOut ? "default" : "pointer",
                opacity: isSoldOut ? 0.55 : 1,
                fontSize: T_NEW.micro,
                fontWeight: 600,
                fontFamily: T_NEW.fontMono,
                textTransform: "uppercase",
                letterSpacing: T_NEW.monoTrack,
                textDecoration: isSoldOut ? "line-through" : "none",
              }}>
              {v.name}
            </button>
          );
        })}
      </div>

      {/* Active variant detail */}
      {active && (
        <div style={{ fontSize: T_NEW.meta, lineHeight: 1.55, color: T_NEW.ink }}>
          {priceRange && (
            <div style={{ marginBottom: 4 }}>
              <Serif size={T_NEW.body} color={T_NEW.ink} style={{ fontWeight: 500 }}>
                {priceRange}
              </Serif>
              {showMsrp && (
                <span style={{
                  color: T_NEW.ink38,
                  marginLeft: 8,
                  textDecoration: "line-through",
                  fontSize: T_NEW.meta,
                }}>
                  ${(active.msrp_cents / 100).toFixed(2)}
                </span>
              )}
            </div>
          )}
          {activeSizes.length > 0 ? (
            <div style={{ color: T_NEW.ink56 }}>
              <span>{t("post.variants.sizes")}</span>{" "}
              <span style={{ color: T_NEW.ink, fontFamily: T_NEW.fontMono, fontSize: T_NEW.meta }}>
                {activeSizes.join(" · ")}
              </span>
            </div>
          ) : (
            <Mono color={T_NEW.ink56}>{t("post.variants.soldOut")}</Mono>
          )}
          {lowStock && (
            <Mono color={T_NEW.persimmon} style={{ marginTop: 6 }}>
              {t(plural(active.ship_quantity, "post.variants.lowStock"), { n: active.ship_quantity })}
            </Mono>
          )}
        </div>
      )}
    </div>
  );
}

// ─── GroupBuyBlock — ProgressArc + countdown ─────────────────────────
function GroupBuyBlock({ post, t }) {
  const locked = !!post.lockedAt;
  const target = post.targetQty;
  const committed = post.committedQty ?? 0;
  const remainingMs = post.deadlineAt ? (post.deadlineAt * 1000 - Date.now()) : null;
  const remainingLabel = remainingMs == null ? null
    : remainingMs <= 0 ? t("post.gb.expired")
    : (() => {
        const s = Math.floor(remainingMs / 1000);
        const d = Math.floor(s / 86400);
        const h = Math.floor((s % 86400) / 3600);
        const m = Math.floor((s % 3600) / 60);
        if (d > 0) return t("channelfeed.gb.remainDays", { d, h });
        if (h > 0) return t("channelfeed.gb.remainHours", { h, m });
        return t("channelfeed.gb.remainMin", { m });
      })();
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 14,
      background: locked ? T_NEW.dangerSoft : T_NEW.paperSoft,
      borderRadius: T_NEW.r2,
      padding: "10px 14px",
      marginTop: 10,
      border: `1px solid ${locked ? T_NEW.danger : T_NEW.ink12}`,
    }}>
      {target ? (
        <ProgressArc value={committed} total={target} size={72}
                     color={locked ? T_NEW.danger : T_NEW.persimmon}
                     trackColor={locked ? `${T_NEW.danger}30` : T_NEW.ink12}
                     labelTop={t("channelfeed.gb.targetLabel")} />
      ) : (
        <div style={{
          width: 72, height: 72, borderRadius: "50%",
          background: T_NEW.persimmonSoft,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Mono color={T_NEW.persimmon} size={T_NEW.meta}>{t("channelfeed.gb.gbLabel")}</Mono>
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Mono color={locked ? T_NEW.danger : T_NEW.ink56} style={{ display: "block", marginBottom: 4 }}>
          {locked ? t("post.gb.locked") : t("post.gb.open")}
        </Mono>
        <div style={{ fontSize: T_NEW.body, color: T_NEW.ink, fontFamily: T_NEW.fontSans, lineHeight: 1.4 }}>
          {t("post.gb.committed", { n: committed })}
          {target ? t("channelfeed.gb.outOf", { n: target }) : ""}
          {remainingLabel ? ` · ${remainingLabel}` : ""}
        </div>
      </div>
    </div>
  );
}

// ─── StaffRow — moderation controls (manager+ / admin) ───────────────
function StaffRow({ p, t, toast, onAfterPostChange }) {
  const [editing, setEditing] = useState(false);
  const toneFor = (s) =>
    s === "published" ? "bottle"
    : s === "rejected" || s === "archived" ? "danger"
    : "ochre";
  return (
    <div style={{
      marginTop: 14, padding: "10px 12px",
      background: T_NEW.paper,
      borderRadius: T_NEW.r1,
      border: `1px dashed ${T_NEW.ink12}`,
      display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
    }}>
      <StatusPill tone={toneFor(p.status)}>{p.status}</StatusPill>

      {/* Full edit — price, title, RU/ES/EN descriptions, photos, channel. */}
      <button onClick={() => setEditing(true)} style={solidBtn(T_NEW.ink, T_NEW.paperRaised)}>Edit</button>

      {/* Restore an archived post back to published (un-archive). */}
      {p.status === "archived" && (
        <button onClick={async () => {
          try { await api.publishPost(p.id); onAfterPostChange?.(); }
          catch (e) { toast.error(e.message); }
        }} style={solidBtn(T_NEW.ink, T_NEW.paperRaised)}>Restore</button>
      )}

      {p.mode === "group_buy" && p.status === "published" && (
        <>
          {!p.lockedAt && (
            <button onClick={async () => {
              if (!await toast.confirm("STOP this joint purchase? No new orders will be accepted.")) return;
              try { await api.lockPost(p.id); onAfterPostChange?.(); toast.success("Locked."); }
              catch (e) { toast.error(e.message); }
            }} style={ghostBtn(T_NEW.danger)}>STOP</button>
          )}
          <button onClick={async () => {
            if (!await toast.confirm("Lock the post AND confirm every pending order in one go?")) return;
            try {
              const r = await api.confirmPendingOrders(p.id);
              onAfterPostChange?.();
              toast.success(`Confirmed ${r.advancedCount} order(s).`);
            } catch (e) { toast.error(e.message); }
          }} style={solidBtn(T_NEW.ink, T_NEW.paperRaised)}>СТОП + Confirm pending</button>
        </>
      )}

      {p.status !== "published" && p.status !== "rejected" && p.status !== "archived" && (
        <button onClick={async () => { await api.publishPost(p.id); onAfterPostChange?.(); }}
                style={solidBtn(T_NEW.ink, T_NEW.paperRaised)}>Publish</button>
      )}
      {p.status !== "rejected" && p.status !== "archived" && (
        <button onClick={async () => {
          const r = await toast.prompt("Rejection reason?", { placeholder: "Why is this post being rejected?", multiline: true });
          if (r === null) return;
          try { await api.rejectPost(p.id, r); onAfterPostChange?.(); } catch (e) { toast.error(e.message); }
        }} style={ghostBtn(T_NEW.danger)}>Reject</button>
      )}
      {p.status === "published" && (
        <button onClick={async () => {
          if (!await toast.confirm("Archive this post? It'll be hidden from customers but kept in the database.")) return;
          try { await api.archivePost(p.id); onAfterPostChange?.(); }
          catch (e) { toast.error(e.message); }
        }} style={ghostBtn(T_NEW.danger)}>Archive</button>
      )}
      <button onClick={async () => {
        if (!await toast.confirm(`Permanently delete "${p.title}"? This cannot be undone. Use Archive instead if you might want to recover it.`)) return;
        try { await api.deletePost(p.id); onAfterPostChange?.(); }
        catch (e) { toast.error(e.message); }
      }} style={ghostBtn(T_NEW.danger)}>Delete</button>
      {p.rejectionReason && (
        <span style={{ fontSize: T_NEW.meta, color: T_NEW.danger, fontFamily: T_NEW.fontSans, width: "100%", marginTop: 4 }}>
          Reason: {p.rejectionReason}
        </span>
      )}
      {editing && (
        <WarehouseEditModal
          post={p}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onAfterPostChange?.(); }}
        />
      )}
    </div>
  );
}

const solidBtn = (bg, fg) => ({
  padding: "4px 8px", fontSize: T_NEW.meta,
  background: bg, color: fg, border: "none",
  borderRadius: T_NEW.r1, cursor: "pointer",
  fontWeight: 600, fontFamily: T_NEW.fontSans,
});
const ghostBtn = (color) => ({
  padding: "4px 8px", fontSize: T_NEW.meta,
  background: "transparent", color, border: `1px solid ${color}40`,
  borderRadius: T_NEW.r1, cursor: "pointer",
  fontWeight: 600, fontFamily: T_NEW.fontSans,
});

// Human-readable post age. Original output was "32М"/"5Ч"/"2Д" —
// readable only to people who already knew the unit. Phase-0 user
// feedback (2026-06-02) said "what is 32M?". Switched to a natural
// phrase ("32 min ago" / "32 мин назад"), localized via i18n keys
// that already exist for the admin home timeAgo widget.
function humanAge(epochSec, t, lang) {
  const seconds = Math.floor(Date.now() / 1000 - epochSec);
  if (seconds < 60) return t("channelfeed.age.justNow");
  // Intl.RelativeTimeFormat gives full, correctly-pluralized words per locale
  // ("4 days ago" / "4 дня назад" / "hace 4 días") — no manual plural tables.
  const rtf = new Intl.RelativeTimeFormat(localeFor(lang), { numeric: "always" });
  if (seconds < 3600) return rtf.format(-Math.floor(seconds / 60), "minute");
  if (seconds < 86400) return rtf.format(-Math.floor(seconds / 3600), "hour");
  if (seconds < 7 * 86400) return rtf.format(-Math.floor(seconds / 86400), "day");
  return new Date(epochSec * 1000).toLocaleDateString(localeFor(lang), { day: "numeric", month: "long" });
}
